import { WaveBlockKind } from "./types.js";
import { collapsedBlockPreviewLabel, } from "./waveThreadProjection.js";
import { isAgentWidgetRelation } from "./waveWhaleHelpers.js";
const TOC_DESCRIPTION_MAX_CHARS = 200;
/** Human label for a block kind (Italian, UI parity with archived panel). */
export function waveBlockKindLabel(kind) {
    switch (kind) {
        case WaveBlockKind.Text:
            return "Testo";
        case WaveBlockKind.Heading:
            return "Titolo";
        case WaveBlockKind.Decision:
            return "Decisione";
        case WaveBlockKind.Signoff:
            return "Sign-off";
        case WaveBlockKind.Poll:
            return "Sondaggio";
        case WaveBlockKind.Task:
            return "Task";
        case WaveBlockKind.TodoList:
            return "Lista todo";
        case WaveBlockKind.Gallery:
            return "Galleria";
        case WaveBlockKind.File:
            return "Allegato";
        case WaveBlockKind.TidePageRef:
            return "Pagina Tide";
        case WaveBlockKind.DriftTodoRef:
            return "Todo Drift";
        case WaveBlockKind.ReefArticleRef:
            return "Articolo Reef";
        case WaveBlockKind.PulseDocRef:
            return "Documento Pulse";
        default:
            return kind;
    }
}
/** Rough markdown-to-plain-text for previews (regex only, no parser). */
function stripMarkdown(text) {
    return text
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/^\s*[-*+]\s+/gm, "")
        .replace(/^\s*>\s?/gm, "")
        .replace(/[*_~`]/g, "")
        .replace(/\s+/g, " ")
        .trim();
}
function truncate(text, max = TOC_DESCRIPTION_MAX_CHARS) {
    return text.length > max ? `${text.slice(0, max)}…` : text;
}
function humanFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes < 0)
        return "";
    if (bytes < 1024)
        return `${bytes} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
    return `${rounded} ${units[unitIndex]}`;
}
function ragStatusLabel(status) {
    switch (status) {
        case "indexed":
            return "indicizzato";
        case "pending":
        case "indexing":
            return "in indicizzazione";
        case "error":
            return "errore indicizzazione";
        default:
            return null;
    }
}
/** Hover preview text per block kind. */
export function blockTocDescription(block) {
    const p = block.payload;
    if (isAgentWidgetRelation(p.relation)) {
        const question = p.agentQuestion?.trim();
        if (question)
            return truncate(question.replace(/\s+/g, " "));
    }
    switch (block.kind) {
        case WaveBlockKind.Heading: {
            const text = p.text?.trim();
            return text ? truncate(stripMarkdown(text)) : undefined;
        }
        case WaveBlockKind.Text: {
            const text = p.text?.trim();
            if (!text)
                return undefined;
            const plain = stripMarkdown(text);
            return plain ? truncate(plain) : undefined;
        }
        case WaveBlockKind.TodoList: {
            const items = p.todoItems ?? [];
            if (items.length === 0)
                return undefined;
            const done = items.filter((item) => item.done).length;
            return `${items.length} attività · ${done} fatte`;
        }
        case WaveBlockKind.Gallery: {
            const items = p.galleryItems ?? [];
            if (items.length === 0)
                return undefined;
            const count = items.length === 1 ? "1 immagine" : `${items.length} immagini`;
            const caption = items[0]?.caption?.trim();
            return caption ? truncate(`${count} · ${caption}`) : count;
        }
        case WaveBlockKind.File: {
            const parts = [];
            const fileName = p.fileName?.trim();
            if (fileName)
                parts.push(fileName);
            if (typeof p.fileSize === "number") {
                const size = humanFileSize(p.fileSize);
                if (size)
                    parts.push(size);
            }
            const rag = ragStatusLabel(p.ragStatus);
            if (rag)
                parts.push(rag);
            return parts.length > 0 ? truncate(parts.join(" · ")) : undefined;
        }
        case WaveBlockKind.Poll: {
            const choices = (p.choices ?? []).filter((c) => c.trim().length > 0);
            return choices.length > 0 ? truncate(choices.join(" · ")) : undefined;
        }
        case WaveBlockKind.TidePageRef:
        case WaveBlockKind.DriftTodoRef:
        case WaveBlockKind.ReefArticleRef:
        case WaveBlockKind.PulseDocRef: {
            const refTitle = p.refTitle?.trim();
            return refTitle ? truncate(refTitle) : undefined;
        }
        default: {
            const text = p.text?.trim();
            return text ? truncate(stripMarkdown(text)) : undefined;
        }
    }
}
/** TOC entry title: explicit payload title, then collapsed preview, then kind label. */
export function blockTocTitle(block) {
    if (!isAgentWidgetRelation(block.payload.relation)) {
        const title = block.payload.title?.trim();
        if (title)
            return title;
    }
    const preview = collapsedBlockPreviewLabel(block);
    // collapsedBlockPreviewLabel falls back to the raw kind string.
    if (preview === block.kind)
        return waveBlockKindLabel(block.kind);
    return preview;
}
/** Build TOC entries from the same rows the feed renders (single source of truth). */
export function buildThreadToc(rows) {
    return rows.map((row) => {
        const block = row.block;
        const entry = {
            blockId: row.coalescedSourceBlockIds?.[0] ?? block.id,
            depth: row.depth,
            kind: block.kind,
            title: blockTocTitle(block),
        };
        const parentId = block.payload.parentId;
        if (parentId)
            entry.parentId = parentId;
        const relation = row.relation ?? block.payload.relation;
        if (relation)
            entry.relation = relation;
        const description = blockTocDescription(block);
        if (description)
            entry.description = description;
        const authorName = block.payload.authorName?.trim();
        if (authorName)
            entry.authorName = authorName;
        if (block.createdAt)
            entry.createdAt = block.createdAt;
        return entry;
    });
}
